import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import { PERMISSIONS } from '@/constants/auth.constants';
import TicketAssignee from '@/features/TicketAssignee';
import {
  CATEGORY_ID,
  isKanatiCategory,
  SUBCATEGORY_ID
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import { TEAM_QUEUE } from '@/features/TicketCreateGeneralInformation/TicketCreateGeneralInformation.constants';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  AI_SOC_TEAM_QUEUE,
  useFetchAssigneesList,
  useFetchOwnersList
} from '@/pages/Ticket';
import { isLayoutMobileAtom } from '@/store/layout';
import { userIsInternalAtom } from '@/store/user.store';

import useStyles from '../TicketCreateDialog.styles';

const ServiceFormInternal = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [isInternalUser] = useAtom(userIsInternalAtom);
  const [{ label: selectedOrgLabel }] = useAtom(selectedOrganizationAtom);
  const { data: settings } = useFetchSettings();
  const { isFetching: isFetchingOwners, data: owners } = useFetchOwnersList();
  const { isFetching: isFetchingAssignees, data: assignees } =
    useFetchAssigneesList(selectedOrgLabel);
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();
  const { classes } = useStyles({ errors });

  const [teamQueue, ticketCategory, ticketSubcategory, assignee] = useWatch({
    control,
    name: ['team_queue', 'ticket_category', 'ticket_subcategory', 'assignee']
  });

  //-----------------------------------------------------------------
  // business logic to conditionally set team queue select value
  useEffect(() => {
    // category changes
    if (isInternalUser && isKanatiCategory(ticketCategory)) {
      setValue('team_queue', AI_SOC_TEAM_QUEUE);
      return;
    }
    if (ticketCategory === CATEGORY_ID.ESCALATION_REQUEST) {
      setValue('team_queue', 'SOC');
      return;
    }
    if (ticketCategory === CATEGORY_ID.LOG_RESTORE_EXPORT) {
      setValue('team_queue', 'TAM');
      return;
    }
    if (ticketCategory === CATEGORY_ID.VULNERABILITY_MANAGEMENT) {
      setValue('team_queue', 'VMP');
      return;
    }

    // sub-category changes
    if (
      ticketCategory === CATEGORY_ID.CHANGE_REQUEST &&
      (ticketSubcategory === SUBCATEGORY_ID.INTERNAL_NETWORKS ||
        ticketSubcategory === SUBCATEGORY_ID.TRAVEL_NOTIFICATION)
    ) {
      setValue('team_queue', 'SOC Engineering');
    }
  }, [isInternalUser, setValue, ticketCategory, ticketSubcategory]);

  // business logic to conditionally set owner select value
  useEffect(() => {
    const socTeamUser = owners.find((item) => item.name === 'SOC Team');
    if (teamQueue === 'SOC' && socTeamUser !== undefined) {
      setValue('owner_id', socTeamUser?.id);
    }
    setValue('owner_id', -1);
  }, [owners, teamQueue]);

  const handleAssigneeChange = (newAssigneeId: number | null) => {
    if (newAssigneeId && assignees && assignees.length > 0) {
      const assigneeObject =
        assignees.find((a) => a.id === newAssigneeId) || null;
      setValue('assignee', assigneeObject);
    } else {
      setValue('assignee', null);
    }
  };

  return (
    <>
      <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
        <Divider />
        {/* First Row */}
        <Stack direction={isLayoutMobile ? 'column' : 'row'} spacing={2}>
          <Stack className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="team-queue-select">Team Queue *</InputLabel>
            <Controller
              control={control}
              name="team_queue"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <FormControl fullWidth>
                  <Select
                    id="team-queue-select"
                    error={!!formState.errors.team_queue}
                    {...field}
                  >
                    {TEAM_QUEUE.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>

          <Stack className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="followup-picker">Follow-Up *</InputLabel>
            <Controller
              control={control}
              name="followup_date"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <FormControl id="followup-picker" fullWidth>
                  <DatePicker
                    disablePast
                    slotProps={{
                      textField: {
                        error: !!formState.errors.followup_date
                      }
                    }}
                    {...field}
                  />
                </FormControl>
              )}
            />
          </Stack>
        </Stack>

        {/* Second Row */}
        <Stack
          direction={isLayoutMobile ? 'column' : 'row'}
          spacing={2}
          sx={{ mt: 2 }}
        >
          {!isFetchingOwners && (
            <Stack className={classes.ticketCreateDialogFormElement}>
              <InputLabel htmlFor="owner-select">Owner</InputLabel>
              <Controller
                control={control}
                name="owner_id"
                render={({ field }) => {
                  const { onChange, value } = field;
                  return (
                    <Autocomplete
                      {...field}
                      getOptionLabel={(option) => option.name}
                      onChange={(_, newValue) => {
                        onChange(newValue ? newValue.id : null);
                      }}
                      options={owners}
                      renderInput={(params) => <TextField {...params} />}
                      renderOption={(renderOptionProps, option) => (
                        <Box
                          component="li"
                          {...renderOptionProps}
                          sx={{ typography: 'body1' }}
                        >
                          {option.name}
                        </Box>
                      )}
                      value={
                        value
                          ? owners.find((option) => {
                              return value === option.id;
                            })
                          : null
                      }
                    />
                  );
                }}
              />
            </Stack>
          )}

          <Stack className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="jira-input">Jira ID</InputLabel>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="external_ticket_id"
                render={({ field, formState }) => (
                  <TextField
                    id="jira-input"
                    error={!!formState.errors.external_ticket_id}
                    slotProps={{
                      htmlInput: {
                        maxLength: 20,
                        style: { textTransform: 'uppercase' }
                      }
                    }}
                    {...field}
                  />
                )}
              />
            </FormControl>
          </Stack>

          {!isFetchingAssignees && settings.enable_ticket_assignee && (
            <Stack className={classes.ticketCreateDialogFormElement}>
              <InputLabel htmlFor="assignee-select">Assignee</InputLabel>
              <TicketAssignee
                assigneeOptions={assignees}
                assignee={assignee}
                onChange={handleAssigneeChange}
              />
            </Stack>
          )}
        </Stack>
      </CheckPermissions>

      {/* Customer view */}
      <CheckPermissions permissions={PERMISSIONS.NOT_INTERNAL}>
        {!isFetchingAssignees && settings.enable_ticket_assignee && (
          <Stack className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="assignee-select">Assignee</InputLabel>
            <TicketAssignee
              assigneeOptions={assignees}
              assignee={assignee}
              onChange={handleAssigneeChange}
            />
          </Stack>
        )}
      </CheckPermissions>
    </>
  );
};

export default ServiceFormInternal;
